import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { PORTFOLIO } from "@/data/content";
import { motion, AnimatePresence } from "framer-motion";

const FILTERS = ["All", "Music Photography", "Cinematic Video", "Film Production"];

export default function Portfolio() {
  const [active, setActive] = useState("All");

  const items = useMemo(
    () => (active === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === active)),
    [active]
  );

  return (
    <section
      id="portfolio"
      data-testid="portfolio-section"
      className="relative bg-[#050505] py-24 md:py-32 px-6 md:px-12 lg:px-24 border-t border-white/10"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 md:mb-20">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#E50914] mb-6">
            // 02 — Selected Work
          </p>
          <h2
            data-testid="portfolio-title"
            className="font-display uppercase text-white text-5xl sm:text-6xl md:text-7xl leading-[0.9]"
          >
            Frames that <br /> carry the record.
          </h2>
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setActive(f)}
              data-testid={`portfolio-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              className={`font-mono text-[11px] uppercase tracking-[0.22em] px-4 py-2 border transition-colors duration-200 ${
                active === f
                  ? "bg-white text-black border-white"
                  : "bg-transparent text-zinc-300 border-white/20 hover:border-white/60"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-[280px] md:auto-rows-[260px] gap-4 md:gap-6">
        <AnimatePresence mode="popLayout">
          {items.map((item, i) => (
            <motion.div
              layout
              key={item.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.55, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] }}
              className={item.span}
            >
              <Link
                to={`/work/${item.id}`}
                data-testid={`portfolio-item-${item.id}`}
                className="portfolio-tile group relative block h-full w-full border border-white/10 hover:border-white/30 bg-black"
              >
                <img
                  src={item.image}
                  alt={item.title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                <div className="tile-overlay absolute inset-0 flex flex-col justify-between p-5 md:p-7">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/80 bg-black/40 backdrop-blur-sm px-2 py-1 border border-white/10">
                      {item.category}
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">
                      {item.year}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-display uppercase text-white text-2xl md:text-4xl leading-none">
                      {item.title}
                    </h3>
                    <div className="mt-3 w-10 h-[2px] bg-[#E50914] group-hover:w-20 transition-[width] duration-500" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </section>
  );
}
